import {Component, HostListener, OnDestroy, OnInit} from "@angular/core";
import {MatSnackBar} from '@angular/material/snack-bar';
import {TdDialogService} from '@covalent/core/dialogs';
import {Router} from '@angular/router';
import {StaffAttendanceService} from "../../../@services/staff-attendance.service";
import {AuthService} from "../../../@services/auth.service";
import {NotificationService} from "../../../@services/notification.service";
import {LocalstorageService} from "../../../@services/localstorage.service";
import {ClockConfirmDialogComponent} from "../clock-confirm-dialog/clock-confirm-dialog.component";
import {interval, Subscription} from "rxjs";

@Component({
  selector: 'app-staff-attendance-list',
  templateUrl: './staff-attendance-list.component.html',
  styleUrls: ['./staff-attendance-list.component.scss'],
})
export class StaffAttendanceListComponent implements OnInit, OnDestroy {
  title: string = 'Staff Attendance';
  isMobile: boolean = false;

  // Staff lists
  allStaff: any[] = [];
  currentlyWorking: any[] = [];
  availableStaff: any[] = [];
  search: string = '';

  // NFC
  nfcSupported: boolean = false;
  nfcEnabled: boolean = false;

  // Auto-refresh
  private refreshSub: Subscription;

  // Section collapse states
  currentlyWorkingExpanded: boolean = true;
  availableStaffExpanded: boolean = true;
  historyExpanded: boolean = false;

  // Attendance History
  attendanceHistory: any[] = [];
  historyFilter: string = 'today';

  merchantId: number;

  constructor(
    private staffAttendanceService: StaffAttendanceService,
    private customAuthService: AuthService,
    private router: Router,
    private sb: MatSnackBar,
    private ns: NotificationService,
    private ls: LocalstorageService,
    private ds: TdDialogService,
  ) {
    this.checkIsMobile();
  }

  ngOnInit(): void {
    this.merchantId = this.ls.getMerchant().id;
    this.loadCurrentlyWorking();
    this.loadAllStaff();
    this.loadAttendanceHistory();
    this.loadSettings();

    // Auto-refresh every 30 seconds to update durations
    this.refreshSub = interval(30000).subscribe(() => {
      this.loadCurrentlyWorking();
    });

    // Check NFC support
    this.checkNfcSupport();
  }

  ngOnDestroy(): void {
    if (this.refreshSub) {
      this.refreshSub.unsubscribe();
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.checkIsMobile();
  }

  private checkIsMobile() {
    this.isMobile = window.innerWidth < 768;
  }

  loadCurrentlyWorking() {
    this.staffAttendanceService.getCurrentlyWorking(this.merchantId).subscribe(
      (res) => {
        this.currentlyWorking = res || [];
        this.updateAvailableStaff();
      },
      (error) => {
        console.error('Error loading currently working', error);
      }
    );
  }

  loadAllStaff() {
    this.staffAttendanceService.getAllStaff(this.merchantId).subscribe(
      (res) => {
        this.allStaff = res?.content || [];
        this.updateAvailableStaff();
      },
      (error) => {
        console.error('Error loading staff', error);
      }
    );
  }

  updateAvailableStaff() {
    const workingIds = new Set(this.currentlyWorking.map(w => w.staffId));
    this.availableStaff = this.allStaff.filter(s => !workingIds.has(s.id));
  }

  get filteredAvailableStaff() {
    if (!this.search) return this.availableStaff;
    const term = this.search.toLowerCase();
    return this.availableStaff.filter(s =>
      s.name?.toLowerCase().includes(term) || s.staffNo?.toLowerCase().includes(term)
    );
  }

  get filteredCurrentlyWorking() {
    if (!this.search) return this.currentlyWorking;
    const term = this.search.toLowerCase();
    return this.currentlyWorking.filter(w =>
      w.staffName?.toLowerCase().includes(term) || w.staffNo?.toLowerCase().includes(term)
    );
  }

  loadSettings() {
    this.staffAttendanceService.getSettings(this.merchantId).subscribe(
      (res) => {
        const methods: string[] = res?.enabledMethods ? JSON.parse(res.enabledMethods) : ['LIST'];
        this.nfcEnabled = methods.includes('NFC');
      },
      () => {
        this.nfcEnabled = false;
      }
    );
  }

  // Clock In - opens confirm dialog
  onClockIn(staff: any) {
    const dialogRef = this.ds.open(ClockConfirmDialogComponent, {
      minWidth: this.isMobile ? '95vw' : '400px',
      data: {
        mode: 'clockIn',
        staffName: staff.name,
        staffNo: staff.staffNo,
        staffId: staff.id,
        nfcEnabled: this.nfcEnabled,
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirmed) {
        if (result.method === 'NFC' && result.nfcCardId) {
          // Validate NFC card belongs to selected staff
          this.validateAndClockInByNfc(staff, result.nfcCardId);
        } else {
          this.performClockIn(staff.id, 'LIST');
        }
      }
    });
  }

  // Clock Out - opens confirm dialog
  onClockOut(working: any) {
    const dialogRef = this.ds.open(ClockConfirmDialogComponent, {
      minWidth: this.isMobile ? '95vw' : '400px',
      data: {
        mode: 'clockOut',
        staffName: working.staffName,
        staffNo: working.staffNo,
        staffId: working.staffId,
        attendanceId: working.attendanceId,
        clockIn: working.clockIn,
        durationMinutes: working.durationMinutes,
        nfcEnabled: this.nfcEnabled,
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.confirmed) {
        if (result.method === 'NFC' && result.nfcCardId) {
          // Validate NFC card belongs to selected staff
          this.validateAndClockOutByNfc(working, result.nfcCardId);
        } else {
          this.performClockOut(working.attendanceId, 'LIST');
        }
      }
    });
  }

  private performClockIn(staffId: number, method: string) {
    let dialogRef = this.ns.showNotification({ message: 'Clocking in...', icon: 'fa fa-sync fa-spin', manualClose: true });

    this.staffAttendanceService.clockIn(staffId, this.merchantId, method).subscribe(
      (res) => {
        dialogRef.close();
        this.ns.showNotification({ message: 'Clock in successful!' });
        this.loadCurrentlyWorking();
        this.loadAllStaff();
        this.loadAttendanceHistory();
      },
      (error) => {
        dialogRef.close();
        this.ns.showNotification({ message: error?.error?.message || 'Clock in failed', timeout: 3000 });
      }
    );
  }

  private performClockOut(attendanceId: number, method: string) {
    let dialogRef = this.ns.showNotification({ message: 'Clocking out...', icon: 'fa fa-sync fa-spin', manualClose: true });

    this.staffAttendanceService.clockOut(attendanceId, method).subscribe(
      (res) => {
        dialogRef.close();
        this.ns.showNotification({ message: 'Clock out successful!' });
        this.loadCurrentlyWorking();
        this.loadAllStaff();
        this.loadAttendanceHistory();
      },
      (error) => {
        dialogRef.close();
        this.ns.showNotification({ message: error?.error?.message || 'Clock out failed', timeout: 3000 });
      }
    );
  }

  // Validate NFC card belongs to selected staff before clock in
  private validateAndClockInByNfc(selectedStaff: any, nfcCardId: string) {
    // Find the staff that owns this NFC card
    const staffWithCard = this.allStaff.find(s => s.nfcCardId === nfcCardId);

    if (!staffWithCard) {
      this.ns.showNotification({
        message: 'Unregistered card',
        timeout: 3000
      });
      return;
    }

    if (staffWithCard.id !== selectedStaff.id) {
      this.ns.showNotification({
        message: 'Invalid card',
        timeout: 3000
      });
      return;
    }

    // Card is valid - proceed with clock in using regular method with NFC flag
    this.performClockIn(selectedStaff.id, 'NFC');
  }

  // Validate NFC card belongs to selected staff before clock out
  private validateAndClockOutByNfc(working: any, nfcCardId: string) {
    // Find the staff that owns this NFC card
    const staffWithCard = this.allStaff.find(s => s.nfcCardId === nfcCardId);

    if (!staffWithCard) {
      this.ns.showNotification({
        message: 'Unregistered card',
        timeout: 3000
      });
      return;
    }

    if (staffWithCard.id !== working.staffId) {
      this.ns.showNotification({
        message: 'Invalid card',
        timeout: 3000
      });
      return;
    }

    // Card is valid - proceed with clock out using regular method with NFC flag
    this.performClockOut(working.attendanceId, 'NFC');
  }

  // NFC Support
  checkNfcSupport() {
    this.nfcSupported = typeof (window as any).ReactNativeWebView !== 'undefined';
  }

  handleNfcTap(nfcCardId: string) {
    let dialogRef = this.ns.showNotification({ message: 'Processing NFC...', icon: 'fa fa-sync fa-spin', manualClose: true });

    // Try clock-in first; if already clocked in (backend returns specific error), try clock-out
    this.staffAttendanceService.clockInByNfc(nfcCardId, this.merchantId).subscribe(
      (res) => {
        dialogRef.close();
        this.ns.showNotification({ message: `${res?.staff?.name || 'Staff'} clocked in!` });
        this.loadCurrentlyWorking();
        this.loadAllStaff();
        this.loadAttendanceHistory();
      },
      (error) => {
        const errorMsg = error?.error?.message || '';
        // Only fall back to clock-out when the error specifically says "already clocked in"
        if (errorMsg.toLowerCase().includes('already clocked in')) {
          this.staffAttendanceService.clockOutByNfc(nfcCardId, this.merchantId).subscribe(
            (res) => {
              dialogRef.close();
              this.ns.showNotification({ message: `${res?.staff?.name || 'Staff'} clocked out!` });
              this.loadCurrentlyWorking();
              this.loadAllStaff();
              this.loadAttendanceHistory();
            },
            (err) => {
              dialogRef.close();
              this.ns.showNotification({ message: err?.error?.message || 'NFC error', timeout: 3000 });
            }
          );
        } else {
          dialogRef.close();
          this.ns.showNotification({ message: errorMsg || 'Unrecognized NFC card', timeout: 3000 });
        }
      }
    );
  }

  // Attendance History
  loadAttendanceHistory() {
    const { startDate, endDate } = this.getHistoryDateRange();
    this.staffAttendanceService.getAttendanceHistory(this.merchantId, startDate, endDate).subscribe(
      (res) => {
        // Flatten grouped daily reports into individual sessions
        const grouped = res?.content || [];
        this.attendanceHistory = [];
        for (const day of grouped) {
          for (const session of (day.sessions || [])) {
            this.attendanceHistory.push({
              staffName: day.staffName,
              staffNo: day.staffNo,
              clockIn: session.clockIn,
              clockOut: session.clockOut,
              durationMinutes: session.durationMinutes,
              status: session.clockOut ? 'COMPLETE' : 'INCOMPLETE',
            });
          }
        }
      },
      (error) => {
        console.error('Error loading attendance history', error);
      }
    );
  }

  setHistoryFilter(filter: string) {
    this.historyFilter = filter;
    this.loadAttendanceHistory();
  }

  private getHistoryDateRange(): { startDate: string; endDate: string } {
    const today = new Date();
    const formatDate = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };

    let startDate: Date;
    switch (this.historyFilter) {
      case 'week':
        startDate = new Date(today);
        startDate.setDate(today.getDate() - today.getDay());
        break;
      case 'month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      default: // today
        startDate = new Date(today);
        break;
    }

    return { startDate: formatDate(startDate), endDate: formatDate(today) };
  }

  formatDuration(minutes: number): string {
    if (!minutes) return '0m';
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`;
  }

  formatTime(dateStr: string): string {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    const hrs = d.getHours();
    const mins = String(d.getMinutes()).padStart(2, '0');
    const ampm = hrs >= 12 ? 'PM' : 'AM';
    const h = hrs % 12 || 12;
    return `${h}:${mins} ${ampm}`;
  }
}
